import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { Button, ButtonIcon } from 'primeng/button';

import { TranslationModule } from '@core/translation/translation.module';

/**
 * Horizontal wizard step indicator. Renders a row of numbered steps as
 * icon + label with a connector line between them. Visited steps are
 * clickable for back-navigation; unvisited steps are disabled.
 *
 * Color logic:
 * - active:    primary (orange) with scale lift + shadow
 * - completed: teal — semantically "done" (PrimeNG has no teal severity)
 * - upcoming:  secondary (grey)
 *
 * PrimeNG button colors are overridden via CSS variables applied through
 * state classes on the inner `<p-button>` — see component SCSS and
 * `@u2b-ui/README.md`.
 */
export interface U2bWizardStep {
    /** Unique step id — used as track key and emitted on selection. */
    id: string;
    /** Material icon name. */
    icon: string;
    /** Tolgee key for the label rendered under the icon. */
    labelKey: string;
}

@Component({
    selector: 'u2b-wizard-stepper',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass, Button, ButtonIcon, TranslationModule],
    host: {
        // `flex-none` (= `flex: none` = `0 0 auto`) so the component keeps
        // its intrinsic height inside a flex-column parent (e.g. a dialog)
        // instead of shrinking with the content area. Self-contained — the
        // parent does not need to know the inner element tag.
        class: 'block bg-p-surface-50 border-b border-p-border flex-none',
    },
    templateUrl: './u2b-wizard-stepper.component.html',
    styleUrls: ['./u2b-wizard-stepper.component.scss'],
})
export class U2bWizardStepperComponent {

    readonly steps = input.required<readonly U2bWizardStep[]>();

    readonly currentId = input.required<string>();

    /**
     * Step ids the user is allowed to navigate to. If null, all steps are
     * clickable (e.g. for free-form wizards).
     */
    readonly visitedIds = input<ReadonlySet<string> | null>(null);

    /**
     * Step ids that have an unresolved validation issue. Marked with a small
     * error badge + red label. `null` (default) shows no markers, so existing
     * callers are unaffected.
     */
    readonly invalidIds = input<ReadonlySet<string> | null>(null);

    readonly stepSelected = output<string>();

    readonly currentIndex = computed(() =>
        this.steps().findIndex(s => s.id === this.currentId()),
    );

    isReachable(id: string): boolean {
        const visited = this.visitedIds();
        return visited === null || visited.has(id);
    }

    isInvalid(id: string): boolean {
        const invalid = this.invalidIds();
        return invalid !== null && invalid.has(id);
    }

    onStepClick(id: string): void {
        if (this.isReachable(id)) {
            this.stepSelected.emit(id);
        }
    }
}
